import { User, LoginRequest, RegisterRequest } from '@/types';

// Mock admin credentials
export const MOCK_ADMIN_CREDENTIALS = {
  email: 'admin@enterprise.com',
  password: 'admin123',
};

// Mock demo credentials
export const MOCK_DEMO_CREDENTIALS = {
  email: 'demo@example.com',
  password: 'demo123',
};

// Mock users database
const mockUsers: User[] = [
  {
    id: 'admin-1',
    email: 'admin@enterprise.com',
    name: 'Admin User',
    plan: 'enterprise' as const,
    createdAt: '2024-01-01T00:00:00Z',
    lastLogin: new Date().toISOString(),
  },
  {
    id: 'demo-1',
    email: 'demo@example.com',
    name: 'Demo User',
    plan: 'pro' as const,
    createdAt: '2024-01-15T00:00:00Z',
    lastLogin: new Date().toISOString(),
  },
];

// Mock authentication functions
export const mockAuthService = {
  async login(credentials: LoginRequest): Promise<{ user: User; token: string }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const { email, password, turnstileToken } = credentials;
    
    // In a real implementation, you would validate the turnstileToken here
    // For mock purposes, we'll just log it
    if (turnstileToken) {
      console.log('🛡️ Turnstile token received for login:', turnstileToken.substring(0, 20) + '...');
    }
    
    // Check admin credentials
    if (email === MOCK_ADMIN_CREDENTIALS.email && password === MOCK_ADMIN_CREDENTIALS.password) {
      const user = mockUsers[0];
      return {
        user: { ...user, lastLogin: new Date().toISOString() },
        token: `mock-admin-token-${Date.now()}`,
      };
    }

    // Check demo credentials
    if (email === MOCK_DEMO_CREDENTIALS.email && password === MOCK_DEMO_CREDENTIALS.password) {
      const user = mockUsers[1];
      return {
        user: { ...user, lastLogin: new Date().toISOString() },
        token: `mock-demo-token-${Date.now()}`,
      };
    }

    // Check if user exists in mock database
    const user = mockUsers.find(u => u.email === email);
    if (!user) {
      throw new Error('User not found');
    }

    // For demo purposes, accept any password for existing users
    return {
      user: { ...user, lastLogin: new Date().toISOString() },
      token: `mock-token-${user.id}-${Date.now()}`,
    };
  },

  async register(data: RegisterRequest): Promise<{ user: User; token: string }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // In a real implementation, you would validate the turnstileToken here
    // For mock purposes, we'll just log it
    if (data.turnstileToken) {
      console.log('🛡️ Turnstile token received for registration:', data.turnstileToken.substring(0, 20) + '...');
    }

    // Check if user already exists
    const existingUser = mockUsers.find(u => u.email === data.email);
    if (existingUser) {
      throw new Error('User already exists with this email');
    }

    // Create new user
    const newUser: User = {
      id: `user-${Date.now()}`,
      email: data.email,
      name: data.name,
      plan: (data.plan as 'basic' | 'pro' | 'enterprise') || 'basic',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
    };

    // Add to mock database
    mockUsers.push(newUser);

    return {
      user: newUser,
      token: `mock-token-${newUser.id}-${Date.now()}`,
    };
  },

  async me(token: string): Promise<User> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Extract user ID from token (mock implementation)
    if (token.includes('admin')) {
      return mockUsers[0];
    }
    if (token.includes('demo')) {
      return mockUsers[1];
    }

    // Default to first user for other tokens
    return mockUsers[0];
  },

  async logout(): Promise<void> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    // Nothing to do for mock logout
    return;
  },

  async refreshToken(): Promise<{ token: string }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      token: `mock-refreshed-token-${Date.now()}`,
    };
  },
};

// Mock data generators
export const generateMockUsageStats = () => ({
  totalRequests: 12543,
  successRate: 99.2,
  avgLatency: 145,
  errorCount: 23,
  currentPeriodUsage: 8450,
  usageLimit: 10000,
  dailyUsage: Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    requests: Math.floor(Math.random() * 500) + 100,
    success: Math.floor(Math.random() * 480) + 95,
    errors: Math.floor(Math.random() * 20),
    latency: Math.floor(Math.random() * 100) + 100,
  })),
});

export const generateMockBillingInfo = () => ({
  plan: 'Enterprise',
  status: 'active' as const,
  nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  amount: 99.00,
  currency: 'USD',
  paymentMethod: {
    type: 'card' as const,
    last4: '4242',
    brand: 'Visa',
  },
});

export const generateMockInvoices = () => [
  {
    id: 'inv-001',
    date: '2024-01-01T00:00:00Z',
    amount: 99.00,
    currency: 'USD',
    status: 'paid' as const,
    downloadUrl: '/api/invoices/inv-001/download',
  },
  {
    id: 'inv-002',
    date: '2023-12-01T00:00:00Z',
    amount: 99.00,
    currency: 'USD',
    status: 'paid' as const,
    downloadUrl: '/api/invoices/inv-002/download',
  },
];

export const generateMockCoupons = () => [
  {
    id: 'coupon-1',
    code: 'WELCOME50',
    description: '50% off first month',
    discountType: 'percentage' as const,
    discountValue: 50,
    isActive: true,
    expiresAt: '2024-12-31T23:59:59Z',
    usageCount: 25,
    usageLimit: 100,
  },
  {
    id: 'coupon-2',
    code: 'SAVE10',
    description: '$10 off monthly subscription',
    discountType: 'fixed' as const,
    discountValue: 10,
    isActive: true,
    usageCount: 150,
    usageLimit: 500,
  },
];
